/**
 * Retorno a la pantalla de autorización del connect-flow después del login.
 *
 * La tienda (plugin Woo/PrestaShop o la app de Shopify) manda al comercio a
 * /home/integrations/connect con ?platform=&shop=&state=&callback=. Si no tiene
 * sesión, el portal lo manda a /login y, sin esto, tras ingresar aterrizaba en
 * /home: la solicitud de la tienda se perdía y tenía que volver a empezar desde
 * su admin, sin saber por qué.
 *
 * Se guarda en sessionStorage porque el login termina en una recarga completa
 * (window.location.replace) y porque el retorno pertenece a ESTA pestaña: otra
 * pestaña con su propio login no debe heredarlo.
 *
 * Seguridad: lo guardado se trata como no confiable al leerlo. Solo se acepta
 * una URL del mismo origen y con el pathname EXACTO de la pantalla de conexión;
 * cualquier otra cosa se descarta, para que esto no sea un open redirect.
 */

export const CONNECT_RETURN_PATH = "/home/integrations/connect";

// Igual al vencimiento del `state` de la app de Shopify (STATE_TTL_SECONDS = 600
// en shopify-beartrack-app): volver después de que venció el enlace de la app
// solo lleva a autorizar un código que la app ya no puede canjear. Con Woo
// (15 min) queda conservador: a lo sumo el comercio reinicia la conexión desde
// su tienda. El TTL del módulo de PrestaShop no está verificado.
export const CONNECT_RETURN_TTL_MS = 10 * 60 * 1000;

export const CONNECT_RETURN_STORAGE_KEY = "beartrack.connectReturn";

/**
 * Recuerda la ubicación actual SOLO si es la pantalla de conexión con su query.
 * Cualquier otra ruta se ignora: no es un "volver a donde estabas" genérico.
 *
 * @param {{ pathname: string, search: string }} [location]
 */
export function rememberConnectReturn(location = window.location) {
  const pathname = location?.pathname;
  const search = location?.search;
  if (pathname !== CONNECT_RETURN_PATH || !search || search === "?") return;

  try {
    sessionStorage.setItem(
      CONNECT_RETURN_STORAGE_KEY,
      JSON.stringify({ url: pathname + search, at: Date.now() }),
    );
  } catch {
    // Storage no disponible (modo privado, cuota): se pierde solo el retorno,
    // el comercio puede volver a iniciar la conexión desde su tienda.
  }
}

/**
 * Revalida una URL guardada: mismo origen y pathname exacto. Devuelve la ruta
 * normalizada (pathname + search) o null.
 */
function sanitizeReturnUrl(raw) {
  if (typeof raw !== "string" || !raw) return null;
  let parsed;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;
  if (parsed.pathname !== CONNECT_RETURN_PATH || !parsed.search) return null;
  return parsed.pathname + parsed.search;
}

function readValidReturn() {
  let raw;
  try {
    raw = sessionStorage.getItem(CONNECT_RETURN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let entry;
  try {
    entry = JSON.parse(raw);
  } catch {
    return null;
  }

  const age = Date.now() - Number(entry?.at);
  if (!(age >= 0 && age <= CONNECT_RETURN_TTL_MS)) return null;

  return sanitizeReturnUrl(entry?.url);
}

/**
 * Retorno pendiente y vigente, SIN consumirlo. Seguro de llamar en un render.
 * @returns {string|null}
 */
export function peekConnectReturn() {
  return readValidReturn();
}

/** Borra el retorno pendiente, si lo hay. */
export function clearConnectReturn() {
  try {
    sessionStorage.removeItem(CONNECT_RETURN_STORAGE_KEY);
  } catch {
    // Storage no disponible: nada que borrar.
  }
}

/**
 * Lee el retorno pendiente y lo borra: se usa una sola vez, al aterrizar tras
 * el login. Un retorno vencido o inválido también se borra.
 * @returns {string|null}
 */
export function consumeConnectReturn() {
  const url = readValidReturn();
  clearConnectReturn();
  return url;
}

/**
 * Destino final de un login exitoso, a partir del destino por rol
 * (resolveLoginDestination). El retorno a la autorización de una tienda solo
 * vale para cuentas de comercio (las que aterrizan en /home). Cualquier otra
 * cuenta lo descarta: no puede autorizar y no debe heredarlo el próximo login
 * de esta pestaña.
 *
 * @param {string|null} destination destino por rol; null si el rol no tiene portal web
 * @returns {string|null} null solo si `destination` era null
 */
export function resolvePostLoginTarget(destination) {
  if (destination === "/home") return consumeConnectReturn() ?? destination;
  clearConnectReturn();
  return destination;
}
